import { IconButton, Menu, MenuItem, MenuList, Stack, Typography, Backdrop, CircularProgress } from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { PopupState as PopupStateType } from 'material-ui-popup-state/hooks'
import { Fragment, useState } from 'react'
import { FileDownloadRounded, MoreVertRounded, RemoveRedEyeRounded } from '@mui/icons-material'
import {
  client,
  QcTimepointContainerSubmission,
  TimepointContainerSubmissionExamMetadataResponse,
  QcTaskState,
  QcSeriesDescription,
} from '@common/config/api/client'
import DeleteModal from '../modals/DeleteModal'
import { SPACING } from '@common/theme/spacing'
import { useDownloadExamMetadataMutation } from '@features/qc_task_details_view/hooks/useDownloadExamMetadataMutation'
import { useParams } from 'react-router-dom'
import { launchDicomViewerForQC } from '@common/utils/ohifUtils'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import ReviewDicomTagsModal from '../modals/ReviewDicomTagsModal'
import { toast } from 'sonner'
import Toast from '@common/components/Toast'
import StudySeriesModal from '../modals/StudySeriesModal'

export default function StudyExamDetailPopupMenuList({
  qcTaskOrder,
  qcTaskStatus,
  timepointSubmissionId,
  seriesDescription,
  studyDescription,
  container,
  exam,
  showViewerOption = true,
}: {
  qcTaskOrder: number
  qcTaskStatus: QcTaskState
  timepointSubmissionId: string
  seriesDescription: QcSeriesDescription
  studyDescription: string
  container?: QcTimepointContainerSubmission
  exam?: TimepointContainerSubmissionExamMetadataResponse
  showViewerOption?: boolean
}) {
  const { qcTaskId, studyId } = useParams()
  const [loading, setLoading] = useState(false)
  const { user } = useExtendedAuth()

  const { mutateAsync: downloadExamMetadata } = useDownloadExamMetadataMutation()

  if (!studyId || !user?.access_token) {
    toast(
      <Toast
        message="Missing required parameters for QC task viewer. Please ensure study ID and authentication token are provided."
        variant="error"
      />,
    )
    return false
  }

  const handledDownloadExamMetadata = async (popupState: PopupStateType) => {
    if (!container || !qcTaskId || !studyId) return

    // Close the menu
    popupState.close()

    // Show loading state
    setLoading(true)
    toast.loading('Preparing download...', { id: 'exam-download' })

    downloadExamMetadata({
      path: {
        qc_task_id: qcTaskId,
      },

      query: {
        timepoint_submission_id: timepointSubmissionId,
        study_id: studyId,
        timepoint_container_submission_id: container?.container_submission_id,
      },
    })
      .then(async (response) => {
        try {
          const fileUrl = response.url
          if (fileUrl) {
            const fileResponse = await client.instance.get(fileUrl, {
              responseType: 'blob',
            })
            if (fileResponse.status !== 200) throw new Error('Failed to fetch file')
            const blob = fileResponse.data
            const blobUrl = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = fileUrl.split('/').pop()?.split('?').shift() || 'exam_metadata.zip'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            toast.success('Download started successfully', { id: 'exam-download' })
          } else {
            throw new Error('Failed to fetch file')
          }
        } catch (error) {
          toast.error(`Error downloading the file: ${error}`, { id: 'exam-download' })
          console.error('Error downloading the file:', error)
        } finally {
          setLoading(false)
        }
      })
      .catch((error) => {
        toast.error('Failed to generate download URL', { id: 'exam-download' })
        console.error('Error fetching download URL:', error)
        setLoading(false)
      })
  }

  return (
    <PopupState variant="popover">
      {(popupState: PopupStateType) => (
        <Fragment>
          <IconButton {...bindTrigger(popupState)}>
            <MoreVertRounded
              sx={{
                color: 'text.secondary',
                width: 20,
                height: 20,
              }}
            />
          </IconButton>
          <Menu
            {...bindMenu(popupState)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            data-testid="popup-menu-list"
          >
            <MenuList
              sx={{
                width: 220,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                paddingY: SPACING.examDetailPopupMenuListSpacing,
                outline: 'none',
              }}
            >
              {qcTaskStatus === 'COMPLETE' && showViewerOption && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  onClick={() => {
                    if (studyId && qcTaskId && user?.access_token) {
                      launchDicomViewerForQC(
                        studyId,
                        qcTaskId,
                        user.access_token,
                        seriesDescription.submission_series_id || undefined,
                      )
                    }
                  }}
                  data-testid="popup-menu-list-item-reopen"
                >
                  <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                    <RemoveRedEyeRounded
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                    <Typography>Open in viewer</Typography>
                  </Stack>
                </MenuItem>
              )}
              {qcTaskStatus === 'COMPLETE' && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  data-testid="popup-menu-list-item-reset"
                >
                  {/* This is the button that opens the popup menu for exam details */}
                  <ReviewDicomTagsModal
                    exam={exam}
                    seriesDescription={seriesDescription}
                    studyDescription={studyDescription}
                    popupState={popupState}
                    readOnly={true}
                  />
                </MenuItem>
              )}
              {qcTaskStatus === 'COMPLETE' && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  onClick={() => handledDownloadExamMetadata(popupState)}
                  disabled={loading}
                  data-testid="popup-menu-list-item-reset"
                >
                  <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
                    <FileDownloadRounded
                      sx={{
                        color: 'text.secondary',
                      }}
                    />
                    <Typography>Download</Typography>
                  </Stack>
                </MenuItem>
              )}
              {qcTaskStatus === 'COMPLETE' && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  data-testid="popup-menu-list-item-reset"
                >
                  <StudySeriesModal
                    studyDescription={studyDescription}
                    seriesDescription={seriesDescription}
                    popupState={popupState}
                  />
                </MenuItem>
              )}
              {qcTaskStatus !== 'COMPLETE' && qcTaskOrder == 1 && (
                <MenuItem
                  sx={{
                    height: 36,
                    width: '100%',
                    paddingY: SPACING.examDetailPopupMenuListSpacing,
                  }}
                  data-testid="popup-menu-list-item-reset"
                >
                  <DeleteModal
                    container_submission_id={container?.container_submission_id}
                    exam={exam}
                    popupState={popupState}
                  />
                </MenuItem>
              )}
            </MenuList>
          </Menu>

          {/* Global loading backdrop */}
          <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
        </Fragment>
      )}
    </PopupState>
  )
}
